<!--
@component
Icons

See markup below.
-->

<script lang="ts">
  import getPath from '$utils/getPath';
  import { isIconName, type IconName } from './iconNames';

  interface Props {
    /**
     * Icon filename (without extension) under `images/icons/`.
     * Restricted at compile time to the actual PNGs present in
     * `src/statics/images/icons/` — see `./iconNames.ts`.
     */
    name?: IconName;
    /** CSS length, e.g. '300px', '2rem', '100%'. */
    width?: string;
    /** CSS length, e.g. '300px', '2rem', '100%'. */
    height?: string;
    /** Class to apply to the rendered <img>. */
    class?: string;
    /** When true, the image is loaded eagerly with high priority. */
    eager?: boolean;
    /**
     * Accessible label. If omitted the icon is treated as decorative
     * and hidden from assistive tech.
     */
    label?: string;
  }

  let {
    name = 'football',
    width = '300px',
    height = '300px',
    class: className = '',
    eager = false,
    label = '',
  }: Props = $props();

  const FALLBACK_NAME: IconName = 'football';
  const fallbackPath = getPath(`images/icons/${FALLBACK_NAME}.png`);

  // Belt-and-braces runtime guard: protects against untyped JS callers
  // passing an unknown name, which the compile-time union can't catch.
  let safeName = $derived(isIconName(name) ? name : FALLBACK_NAME);
  let imgPath = $derived(getPath(`images/icons/${safeName}.png`));

  const handleError = (event: Event) => {
    const img = event.currentTarget as HTMLImageElement;
    if (img.src !== fallbackPath) img.src = fallbackPath;
  };
</script>

<img
  src={imgPath}
  alt={label}
  aria-hidden={label ? undefined : true}
  draggable="false"
  loading={eager ? 'eager' : 'lazy'}
  decoding="async"
  fetchpriority={eager ? 'high' : 'auto'}
  class={className}
  style:width
  style:height
  onerror={handleError}
/>
